import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import itLocale from "date-fns/locale/it";
import enLocale from "date-fns/locale/en-US";
import {format as formatDateFns, isAfter, isBefore, isValid, parse, set as setDateParts} from "date-fns";
import {DateCalendar, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV2";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CalendarMonthTwoToneIcon from "@mui/icons-material/CalendarMonthTwoTone";
import ScheduleTwoToneIcon from "@mui/icons-material/ScheduleTwoTone";
import makeStyles from "@mui/styles/makeStyles";
import {useTheme} from "@mui/material/styles";
import {Controller} from "react-hook-form";
import clsx from "clsx";
import {AppContext} from "../../../App";
import {languages} from "../../../strings/LanguageDictionary";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		minWidth: 0,
	},
	field: {
		"&&": {
			width: "100%",
			minWidth: 0,
		},
		"&& .MuiInputBase-root, && .MuiOutlinedInput-root": {
			minHeight: 40,
			borderRadius: "14px !important",
			background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
			backgroundImage: "none !important",
			backgroundColor: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
			backgroundClip: "padding-box !important",
			backdropFilter: "none !important",
			WebkitBackdropFilter: "none !important",
		},
		"&& .MuiInputBase-input, && .MuiOutlinedInput-input": {
			background: "transparent !important",
			backgroundColor: "transparent !important",
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? "rgba(32, 50, 64, 0.18)" : "rgba(255, 255, 255, 0.16)",
			borderRadius: "14px !important",
		},
		"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? "rgba(47, 99, 255, 0.42)" : "rgba(129, 212, 250, 0.34)",
		},
		"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? "#2f63ff" : "rgba(129, 212, 250, 0.76)",
			borderWidth: "1px !important",
		},
		"& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: `${theme.palette.error.main} !important`,
			borderWidth: "1px !important",
		},
		"& .MuiInputLabel-root": {
			fontWeight: 800,
		},
		"& .MuiInputLabel-root.Mui-error": {
			color: `${theme.palette.error.main} !important`,
		},
		"& .MuiFormHelperText-root": {
			position: "static !important",
			margin: `${theme.spacing(0.45)} 0 0 ${theme.spacing(1.25)} !important`,
			padding: "0 !important",
			color: `${theme.palette.error.main} !important`,
			background: "transparent !important",
			borderRadius: "0 !important",
			boxShadow: "none !important",
			fontSize: "12px !important",
			fontWeight: "750 !important",
			lineHeight: "1.25 !important",
			transform: "none !important",
		},
	},
	popper: {
		zIndex: `${theme.zIndex.modal + 2} !important`,
	},
	paper: {
		width: 336,
		maxWidth: "calc(100vw - 32px)",
		padding: theme.spacing(1),
		background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		backgroundColor: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		backgroundImage: "none !important",
		color: `${theme.palette.text.primary} !important`,
		border: `1px solid ${theme.palette.mode === "light" ? "rgba(32, 50, 64, 0.12)" : "rgba(255, 255, 255, 0.14)"}`,
		borderRadius: "18px !important",
		boxShadow: theme.palette.mode === "light"
			? "0 18px 42px rgba(32, 50, 64, 0.18)"
			: "0 18px 42px rgba(0, 0, 0, 0.46)",
		backdropFilter: "none !important",
		WebkitBackdropFilter: "none !important",
		overflow: "hidden",
	},
	pickerHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 1.25, 0.5),
	},
	pickerTitle: {
		color: theme.palette.text.primary,
		fontSize: 13,
		fontWeight: 900,
		lineHeight: 1.2,
	},
	pickerHint: {
		color: theme.palette.text.secondary,
		fontSize: 11,
		fontWeight: 700,
		lineHeight: 1.2,
	},
	calendar: {
		"& .MuiDateCalendar-root": {
			width: "100%",
			background: "transparent !important",
			color: theme.palette.text.primary,
		},
		"& .MuiPickersCalendarHeader-root": {
			paddingLeft: theme.spacing(1.25),
			paddingRight: theme.spacing(1.25),
		},
		"& .MuiDayCalendar-weekDayLabel": {
			color: theme.palette.text.secondary,
			fontWeight: 850,
		},
		"& .MuiPickersDay-root": {
			borderRadius: "10px !important",
			fontWeight: 750,
		},
		"& .MuiPickersDay-root.Mui-selected": {
			background: `${theme.palette.primary.main} !important`,
			color: `${theme.palette.primary.contrastText} !important`,
			boxShadow: theme.palette.mode === "light"
				? "0 8px 18px rgba(47, 99, 255, 0.24)"
				: "0 8px 18px rgba(129, 212, 250, 0.18)",
		},
		"& .MuiPickersDay-today": {
			borderColor: `${theme.palette.primary.main} !important`,
		},
	},
	timeRow: {
		display: "grid",
		gridTemplateColumns: "minmax(0, 1fr)",
		gap: theme.spacing(0.75),
		padding: theme.spacing(1, 1.25, 1.25),
	},
	timeField: {
		"&& .MuiInputBase-root": {
			minHeight: 40,
			borderRadius: "14px !important",
			background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
		},
		"&& .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? "rgba(32, 50, 64, 0.18)" : "rgba(255, 255, 255, 0.16)",
		},
	},
	actionRow: {
		display: "flex",
		justifyContent: "flex-end",
		gap: theme.spacing(0.75),
		padding: theme.spacing(0.75, 1.25, 1),
	},
	actionButton: {
		borderRadius: "999px !important",
		textTransform: "none !important",
		fontWeight: "850 !important",
	},
}));

const dateFormat = "dd/MM/yyyy";
const dateTimeFormat = "dd/MM/yyyy HH:mm";

const getSolidBackground = (theme) => theme.palette.mode === "light" ? "#fff" : "#18202c";

const coerceDate = (value) => {
	if (!value) return null;
	if (value instanceof Date) return isValid(value) ? value : null;
	const date = new Date(value);
	return isValid(date) ? date : null;
};

const getTypedFormats = (withTime) => (withTime ? [
	"dd/MM/yyyy HH:mm",
	"d/M/yyyy H:mm",
	"dd-MM-yyyy HH:mm",
	"d-M-yyyy H:mm",
	"yyyy-MM-dd HH:mm",
	"yyyy-MM-dd'T'HH:mm",
	"dd/MM/yyyy",
	"d/M/yyyy",
] : [
	"dd/MM/yyyy",
	"d/M/yyyy",
	"dd-MM-yyyy",
	"d-M-yyyy",
	"yyyy-MM-dd",
]);

const normalizeCompactInput = (raw, withTime) => {
	const value = raw.trim();
	if (!withTime && /^\d{8}$/.test(value)) {
		return `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
	}
	if (withTime && /^\d{8}\s+\d{4}$/.test(value)) {
		return `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)} ${value.slice(9, 11)}:${value.slice(11)}`;
	}
	return value;
};

const parseTypedDate = (raw, withTime) => {
	const normalized = normalizeCompactInput(raw, withTime);
	if (!normalized) return null;

	for (const candidateFormat of getTypedFormats(withTime)) {
		const parsed = parse(normalized, candidateFormat, new Date());
		if (isValid(parsed)) {
			if (candidateFormat === "dd/MM/yyyy" && formatDateFns(parsed, "dd/MM/yyyy") !== normalized.padStart(10, "0")) {
				return parsed;
			}
			return parsed;
		}
	}

	const fallback = new Date(normalized);
	return isValid(fallback) ? fallback : null;
};

const isOutsideRange = (date, minDate, maxDate) => (
	(minDate && isBefore(date, minDate)) || (maxDate && isAfter(date, maxDate))
);

const formatDisplayDate = (value, withTime) => {
	const date = coerceDate(value);
	if (!date) return "";
	return formatDateFns(date, withTime ? dateTimeFormat : dateFormat);
};

const getTimeValue = (value) => {
	const date = coerceDate(value);
	return date ? formatDateFns(date, "HH:mm") : "00:00";
};

const mergeDateAndTime = (date, timeValue, fallback) => {
	const [hours = "0", minutes = "0"] = timeValue.split(":");
	return setDateParts(date, {
		hours: Number(hours),
		minutes: Number(minutes),
		seconds: fallback?.getSeconds?.() || 0,
		milliseconds: fallback?.getMilliseconds?.() || 0,
	});
};

const SolidDateField = ({
	ampm,
	className,
	defaultValue,
	disabled,
	error,
	fullWidth,
	handleChange,
	helperText,
	label,
	maxDate,
	minDate,
	name,
	onChange,
	rules,
	selectedValue,
	size,
	value,
	variant,
	withTime,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const appContext = useContext(AppContext);
	const anchorRef = useRef(null);
	const inputRef = useRef(null);
	const rawValue = selectedValue !== undefined ? selectedValue : value;
	const currentDate = useMemo(() => coerceDate(rawValue), [rawValue]);
	const currentDisplayValue = useMemo(() => formatDisplayDate(currentDate, withTime), [currentDate, withTime]);
	const currentTimeValue = useMemo(() => getTimeValue(currentDate), [currentDate]);
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState(formatDisplayDate(currentDate, withTime));
	const inputValueRef = useRef(inputValue);
	const [timeValue, setTimeValue] = useState(getTimeValue(currentDate));
	const [localError, setLocalError] = useState("");
	const locale = appContext.state.lang === "it" ? itLocale : enLocale;
	const solidBackground = getSolidBackground(theme);
	const helperTextStyle = useMemo(() => ({
		position: "static",
		margin: `${theme.spacing(0.45)} 0 0 ${theme.spacing(1.25)}`,
		padding: 0,
		color: theme.palette.error.main,
		background: "transparent",
		borderRadius: 0,
		boxShadow: "none",
		fontSize: 12,
		fontWeight: 750,
		lineHeight: 1.25,
		transform: "none",
	}), [theme]);
	const resolvedHelperText = localError || helperText || (
		error?.type === "validate"
			? languages[appContext.state.lang].OCEAN__GLOBAL_NOT_VALID_FILES
			: error?.message ? languages[appContext.state.lang].OCEAN__GLOBAL_REQUIRED_FILES_ : ""
	);
	const normalizedMinDate = useMemo(() => coerceDate(minDate) || new Date(1900, 1, 1, 1, 1, 1, 1), [minDate]);
	const normalizedMaxDate = useMemo(() => coerceDate(maxDate) || new Date(2100, 1, 1, 1, 1, 1, 1), [maxDate]);

	useEffect(() => {
		setInputValue(currentDisplayValue);
		inputValueRef.current = currentDisplayValue;
		setTimeValue(currentTimeValue);
	}, [currentDisplayValue, currentTimeValue]);

	const emitChange = useCallback((nextDate) => {
		handleChange?.(nextDate);
		onChange?.(nextDate);
	}, [handleChange, onChange]);

	const commitInputValue = useCallback(() => {
		const inputElement = anchorRef.current?.querySelector("input") || inputRef.current;
		const raw = (inputElement?.value ?? inputValueRef.current).trim();
		if (!raw) {
			setLocalError("");
			setInputValue("");
			inputValueRef.current = "";
			if (inputElement) {
				inputElement.value = "";
			}
			emitChange(null);
			return;
		}

		const parsed = parseTypedDate(raw, withTime);
		if (!parsed || isOutsideRange(parsed, normalizedMinDate, normalizedMaxDate)) {
			setLocalError("Data non valida");
			return;
		}

		const normalized = withTime ? mergeDateAndTime(parsed, formatDateFns(parsed, "HH:mm"), currentDate) : parsed;
		setLocalError("");
		const formatted = formatDisplayDate(normalized, withTime);
		setInputValue(formatted);
		inputValueRef.current = formatted;
		if (inputElement) {
			inputElement.value = formatted;
		}
		setTimeValue(getTimeValue(normalized));
		emitChange(normalized);
	}, [currentDate, emitChange, normalizedMaxDate, normalizedMinDate, withTime]);

	const selectDate = (date) => {
		if (!date || isOutsideRange(date, normalizedMinDate, normalizedMaxDate)) return;
		const nextDate = withTime ? mergeDateAndTime(date, timeValue, currentDate) : date;
		const formatted = formatDisplayDate(nextDate, withTime);
		setLocalError("");
		setInputValue(formatted);
		inputValueRef.current = formatted;
		emitChange(nextDate);
		if (!withTime) {
			setOpen(false);
		}
	};

	const updateTime = (nextTime) => {
		setTimeValue(nextTime);
		const baseDate = currentDate || new Date();
		const nextDate = mergeDateAndTime(baseDate, nextTime, currentDate);
		const formatted = formatDisplayDate(nextDate, true);
		setLocalError("");
		setInputValue(formatted);
		inputValueRef.current = formatted;
		emitChange(nextDate);
	};

	const updateTypedValue = useCallback((nextValue) => {
		inputValueRef.current = nextValue;
		setInputValue(nextValue);
		setLocalError("");
	}, []);

	useEffect(() => {
		const input = anchorRef.current?.querySelector("input") || inputRef.current;
		if (!input) return undefined;

		const handleNativeInput = (event) => updateTypedValue(event.target.value);
		const handleNativeBlur = () => commitInputValue();
		input.dataset.solidDateBound = "true";
		input.addEventListener("input", handleNativeInput);
		input.addEventListener("blur", handleNativeBlur);

		return () => {
			input.removeEventListener("input", handleNativeInput);
			input.removeEventListener("blur", handleNativeBlur);
		};
	}, [commitInputValue, updateTypedValue]);

	useEffect(() => {
		const interval = window.setInterval(() => {
			const input = anchorRef.current?.querySelector("input") || inputRef.current;
			if (!input) return;

			if (input.value !== inputValueRef.current) {
				updateTypedValue(input.value);
			}

			if (document.activeElement !== input && input.value) {
				const parsed = parseTypedDate(input.value, withTime);
				if (!parsed || isOutsideRange(parsed, normalizedMinDate, normalizedMaxDate)) {
					commitInputValue();
					return;
				}

				const expectedValue = formatDisplayDate(withTime ? mergeDateAndTime(parsed, formatDateFns(parsed, "HH:mm"), currentDate) : parsed, withTime);
				if (input.value !== expectedValue) {
					commitInputValue();
				}
			}
		}, 200);

		return () => window.clearInterval(interval);
	}, [commitInputValue, currentDate, normalizedMaxDate, normalizedMinDate, updateTypedValue, withTime]);

	return (
		<div className={classes.root} ref={anchorRef}>
			<TextField
				autoComplete="off"
				className={clsx(classes.field, className)}
				data-solid-date-field="true"
				disabled={disabled}
				error={Boolean(error || localError)}
				fullWidth={fullWidth}
				helperText={resolvedHelperText}
				id={name}
				inputRef={inputRef}
				label={label}
				margin="dense"
				name={name}
				onBlur={commitInputValue}
				onChange={(event) => {
					updateTypedValue(event.target.value);
				}}
				onKeyDown={(event) => {
					if (event.key === "Enter") {
						commitInputValue();
					}
				}}
				placeholder={withTime ? "gg/mm/aaaa hh:mm" : "gg/mm/aaaa"}
				size={size}
				slotProps={{
					input: {
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="Apri calendario"
									disabled={disabled}
									edge="end"
									onMouseDown={(event) => event.preventDefault()}
									onClick={() => setOpen((prev) => !prev)}
									size="small"
								>
									{withTime ? <ScheduleTwoToneIcon fontSize="small"/> : <CalendarMonthTwoToneIcon fontSize="small"/>}
								</IconButton>
							</InputAdornment>
						),
						style: {
							background: solidBackground,
							backgroundColor: solidBackground,
							backgroundImage: "none",
							backgroundClip: "padding-box",
							backdropFilter: "none",
							WebkitBackdropFilter: "none",
						},
					},
					htmlInput: {
						inputMode: "numeric",
						"data-solid-date-input": "true",
						onInput: (event) => updateTypedValue(event.currentTarget.value),
						onBlur: commitInputValue,
						ref: inputRef,
						style: {
							background: "transparent",
							backgroundColor: "transparent",
						},
					},
					formHelperText: {
						style: helperTextStyle,
					},
				}}
				value={inputValue}
				variant={variant}
			/>
			<Popper
				anchorEl={anchorRef.current}
				className={classes.popper}
				open={open}
				placement="bottom-start"
			>
				<ClickAwayListener onClickAway={() => setOpen(false)}>
					<Paper className={classes.paper} elevation={8}>
						<div className={classes.pickerHeader}>
							<Box>
								<Typography className={classes.pickerTitle}>
									{withTime ? "Seleziona data e ora" : "Seleziona data"}
								</Typography>
								<Typography className={classes.pickerHint}>
									{withTime ? "Puoi anche scriverla direttamente nel campo." : "Puoi anche scriverla direttamente nel campo."}
								</Typography>
							</Box>
						</div>
						<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
							<Box className={classes.calendar}>
								<DateCalendar
									disabled={disabled}
									maxDate={normalizedMaxDate}
									minDate={normalizedMinDate}
									onChange={selectDate}
									value={currentDate}
								/>
							</Box>
						</LocalizationProvider>
						{withTime && (
							<React.Fragment>
								<Divider/>
								<div className={classes.timeRow}>
									<TextField
										className={classes.timeField}
										fullWidth
										label="Orario"
										onChange={(event) => updateTime(event.target.value)}
										size="small"
										slotProps={{
											input: {
												startAdornment: (
													<InputAdornment position="start">
														<ScheduleTwoToneIcon fontSize="small"/>
													</InputAdornment>
												),
											},
										}}
										type={ampm ? "text" : "time"}
										value={timeValue}
									/>
								</div>
							</React.Fragment>
						)}
						<div className={classes.actionRow}>
							<Button className={classes.actionButton} onClick={() => setOpen(false)} size="small" variant="contained">
								Conferma
							</Button>
						</div>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	);
};

const SolidDatePickerBase = ({
	ampm = false,
	className = "",
	control,
	defaultValue = null,
	disabled,
	error,
	fullWidth = true,
	helperText,
	label,
	maxDate,
	minDate,
	name,
	onChange,
	rules,
	size = "medium",
	value,
	variant = "outlined",
	withTime = false,
}) => {
	const renderField = (selectedValue, handleChange) => (
		<SolidDateField
			ampm={ampm}
			className={className}
			defaultValue={defaultValue}
			disabled={disabled}
			error={error}
			fullWidth={fullWidth}
			handleChange={handleChange}
			helperText={helperText}
			label={label}
			maxDate={maxDate}
			minDate={minDate}
			name={name}
			onChange={onChange}
			rules={rules}
			selectedValue={selectedValue}
			size={size}
			value={value}
			variant={variant}
			withTime={withTime}
		/>
	);

	if (control && name) {
		return (
			<Controller
				name={name}
				control={control}
				defaultValue={defaultValue}
				rules={rules}
				render={({onChange: controllerOnChange, value: controllerValue}) => (
					renderField(controllerValue, controllerOnChange)
				)}
			/>
		);
	}

	return renderField(value, undefined);
};

const SolidDateInput = (props) => (
	<SolidDatePickerBase {...props}/>
);

export const SolidDateTimeInput = (props) => (
	<SolidDatePickerBase {...props} withTime/>
);

export default React.memo(SolidDateInput);
